import { useState } from 'react'

import { Box, Button, Table, Text, useUnsafeMemo } from '@factorialco/deprecated-design-system'
import { FieldArrayRenderProps, FormikProps, useField } from 'formik'
import {
  LegalEntityForSelectFragment,
  SaleInvoiceComplianceExtraFieldsFieldFragment,
  SaleInvoiceUpsertSidepanelQuery,
  TaxRateForSelectFragment,
} from 'generated/resources/graphql'

import { FieldArray } from 'modules/core/hooks/useForm'
import i18n from 'modules/core/lib/i18n'

import LineItemModal from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/LineItemModal'
import {
  complianceExtraFieldsForLineItemId,
  sortedComplianceExtraFieldsForContext,
} from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/lib/complianceExtraFields'

import { LineItemRowTable } from './_LineItemRowTable'
import { SectionTitle } from './_SectionTitle'
import {
  CustomComplianceExtraFieldErrors,
  FormComplianceExtraFields,
  LineItem,
  defaultLineItem,
} from './types'

type FinanceFinancialDocumentComplianceExtraFieldsConfig = NonNullable<
  SaleInvoiceUpsertSidepanelQuery['finance']['financialDocumentComplianceExtraFieldsConfigs']
>[number]

interface LineItemsSectionWithModalProps<
  T extends {
    lineItems: LineItem[]
    complianceExtraFields?: FormComplianceExtraFields | null
    currency?: string
    legalEntity?: LegalEntityForSelectFragment | null
  }
> {
  form: FormikProps<T>
  isCreditNote: boolean
  readOnly: boolean
  defaultTaxRate?: TaxRateForSelectFragment
  complianceExtraFieldsConfig?: FinanceFinancialDocumentComplianceExtraFieldsConfig
  locale: string
}

const LineItemsHeaderCell = ({ value }: { value: string }) => (
  <Text.Normal size="200" weight="medium">
    {value}
  </Text.Normal>
)

export const LineItemsSectionWithModal = <
  T extends {
    lineItems: LineItem[]
    complianceExtraFields?: FormComplianceExtraFields | null
    currency?: string
    legalEntity?: LegalEntityForSelectFragment | null
    customComplianceExtraFieldErrors?: CustomComplianceExtraFieldErrors | null
  }
>({
  form,
  isCreditNote,
  readOnly,
  defaultTaxRate,
  complianceExtraFieldsConfig,
  locale,
}: LineItemsSectionWithModalProps<T>): React.ReactElement => {
  const [showLineItemModal, setShowLineItemModal] = useState<LineItem | null>(null)

  const [_, { value: lineItems }, { setValue: _setValue }] = useField<LineItem[]>('lineItems')

  const handleAddLineItem = () => {
    setShowLineItemModal({
      ...defaultLineItem,
      taxRate: defaultTaxRate,
      id: crypto.randomUUID(),
    })
  }

  const handleDeleteLineItem = (
    arrayHelpers: FieldArrayRenderProps,
    index: number,
    lineItem: LineItem
  ) => {
    arrayHelpers.remove(index)
    form.setFieldValue(
      `complianceExtraFields.fields`,
      form.values.complianceExtraFields?.fields.filter(
        (field) => field.associatedFieldId !== lineItem.id
      )
    )
  }

  const hasComplianceExtraFields = !!complianceExtraFieldsConfig?.fields?.length

  const lineItemComplianceExtraFields = useUnsafeMemo(() => {
    if (!hasComplianceExtraFields) {
      return []
    }
    return sortedComplianceExtraFieldsForContext(complianceExtraFieldsConfig.fields, 'line_item')
  }, [hasComplianceExtraFields, complianceExtraFieldsConfig])

  const formComplianceExtraFieldsForLineItemId = (
    lineItemId: string
  ): SaleInvoiceComplianceExtraFieldsFieldFragment[] => {
    return complianceExtraFieldsForLineItemId(
      lineItemComplianceExtraFields,
      form.values.complianceExtraFields?.fields || [],
      lineItemId
    )
  }

  const complianceExtraFieldErrorsForLineItemId = (lineItemId: string) => {
    return form.values.customComplianceExtraFieldErrors?.lineItems?.[lineItemId]
  }

  return (
    <Box width="full">
      <FieldArray
        name="lineItems"
        render={(arrayHelpers) => (
          <Box key="lineItems" gap="s16">
            <Box
              flexDirection="row"
              justifyContent="spaceBetween"
              alignItems="center"
              alignSelf="center"
              width="full"
            >
              <SectionTitle title={i18n.t('finance.sale_invoices.form.line_items.items')} />
              {!readOnly && (
                <Box paddingTop="s12">
                  <Button onClick={handleAddLineItem} type="secondary" iconName="add">
                    {i18n.t('finance.sale_invoices.form.line_items.add_line_item')}
                  </Button>
                </Box>
              )}
            </Box>
            <Table fullWidth style={{ overflowY: 'auto', height: 'auto' }} autoLayout>
              <Table.Head>
                <Table.Row>
                  <Table.HeadCell paddingX="s4" ellipsis background="grey000White" width={170}>
                    <Box style={{ paddingLeft: '12px' }}>
                      <LineItemsHeaderCell
                        value={i18n.t('finance.sale_invoices.form.line_items.item')}
                      />
                    </Box>
                  </Table.HeadCell>
                  <Table.HeadCell paddingX="s24" ellipsis background="grey000White" width={50}>
                    <Box style={{ paddingRight: '20px' }}>
                      <LineItemsHeaderCell
                        value={i18n.t('finance.sale_invoices.form.line_items.quantity_short')}
                      />
                    </Box>
                  </Table.HeadCell>
                  <Table.HeadCell paddingX="s24" ellipsis background="grey000White">
                    <Box style={{ paddingRight: '20px' }}>
                      <LineItemsHeaderCell
                        value={i18n.t('finance.sale_invoices.form.line_items.price')}
                      />
                    </Box>
                  </Table.HeadCell>
                  <Table.HeadCell paddingX="s24" ellipsis background="grey000White">
                    <Box style={{ paddingRight: '20px' }}>
                      <LineItemsHeaderCell
                        value={i18n.t('finance.sale_invoices.form.line_items.tax')}
                      />
                    </Box>
                  </Table.HeadCell>
                  {lineItemComplianceExtraFields.map((field) => (
                    <Table.HeadCell
                      key={field.name}
                      paddingX="s24"
                      ellipsis
                      background="grey000White"
                    >
                      <Box style={{ paddingRight: '20px' }}>
                        <LineItemsHeaderCell value={field.label} />
                      </Box>
                    </Table.HeadCell>
                  ))}

                  {!readOnly && (
                    <Table.HeadCell
                      paddingX="s6"
                      style={{ width: '1px', whiteSpace: 'nowrap' }}
                      background="grey000White"
                    >
                      <Box overflowY="auto" style={{ scrollbarGutter: 'stable', width: '40px' }} />
                    </Table.HeadCell>
                  )}
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {lineItems.map((lineItem, index) => (
                  <LineItemRowTable
                    key={index}
                    name={`lineItems.${index}`}
                    onDelete={() => handleDeleteLineItem(arrayHelpers, index, lineItem)}
                    onEdit={() => setShowLineItemModal(lineItem)}
                    currency={form.values.currency}
                    enableDelete={lineItems.length > 1}
                    readOnly={readOnly}
                    locale={locale}
                    complianceExtraFields={formComplianceExtraFieldsForLineItemId(
                      String(lineItem.id)
                    )}
                    complianceExtraFieldErrors={complianceExtraFieldErrorsForLineItemId(
                      String(lineItem.id)
                    )}
                  />
                ))}
                {lineItems.length == 0 && (
                  <Table.Row>
                    <Table.Cell colSpan={100}>
                      <Text.Normal size="300" weight="normal" color="radicalRed600">
                        {i18n.t('finance.sale_invoices.form.error_required')}
                      </Text.Normal>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Box>
        )}
      />
      {showLineItemModal && (
        <LineItemModal
          form={form}
          isCreditNote={isCreditNote}
          lineItem={showLineItemModal}
          currency={form.values.currency}
          country={form.values.legalEntity?.country}
          onClose={() => setShowLineItemModal(null)}
          complianceExtraFieldsConfig={complianceExtraFieldsConfig}
          complianceExtraFieldErrors={complianceExtraFieldErrorsForLineItemId(
            String(showLineItemModal.id)
          )}
        />
      )}
    </Box>
  )
}
